import { LonaWebComponent, html } from "../../component";
import { component } from "../../component-decorators";
import { css } from "../../component-styles";
import { DomUtils } from "../../dom";

@component({ name: "std-landing-cta-block" })
export class LandingCtaBlock extends LonaWebComponent {
  private $heading: HTMLElement = this.$("heading");
  private $subtext: HTMLElement = this.$("subtext");
  private $actions: HTMLElement = this.$("actions");

  static makeWith(props: LandingCtaBlock.Props): LandingCtaBlock {
    return (LandingCtaBlock.make() as LandingCtaBlock).bind(props);
  }

  bind(props: LandingCtaBlock.Props): this {
    this.$heading.textContent = props.heading;
    this.$subtext.textContent = props.subtext ?? "";
    DomUtils.clearChildren(this.$actions);

    this.$actions.appendChild(this.$link(props.primaryCta, true));
    if (props.secondaryCta) {
      this.$actions.appendChild(this.$link(props.secondaryCta, false));
    }

    return this;
  }

  private $link(cta: LandingCtaBlock.Cta, primary: boolean): HTMLElement {
    const $link = document.createElement("a");
    $link.className = primary ? "link-button primary" : "link-button";
    $link.href = cta.href;
    $link.textContent = cta.label;
    return $link;
  }

  static $styles = [
    css`
      :host {
        display: grid;
        gap: 24px;
        max-width: 820px;
        margin: 0 auto;
        padding: 34px;
        text-align: center;
        box-sizing: border-box;
        background: var(--bg);
      }

      #heading,
      #subtext {
        --margin-top: 0px;
        --margin-bottom: 0px;
        font-family: inherit;
      }

      #heading {
        --font-size: var(--text-24px);
        --line-height-ratio: 1.06;
        color: var(--text-pri);
      }

      #subtext {
        --font-size: var(--text-14px);
        --line-height-ratio: 1.52;
        color: var(--text-sec);
      }

      #subtext:empty {
        display: none;
      }

      #actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px;
      }

      .link-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 36px;
        color: var(--text-pri);
        background: var(--bg);
        border: 1px solid var(--divider);
        border-radius: 6px;
        padding: 0 14px;
        text-decoration: none;
        --font-size: var(--text-10px);
        --line-height-ratio: 1;
        font-weight: 550;
        transition: border-color 0.3s ease, box-shadow 0.3s ease;
      }

      .link-button:hover {
        border-color: var(--text-ter);
        box-shadow: var(--box-shadow-light);
      }

      .link-button:focus-visible {
        outline: 2px solid var(--blue);
        outline-offset: 2px;
      }

      .link-button.primary {
        color: var(--bg);
        background: var(--text-pri);
        border-color: var(--text-pri);
      }

      @media (max-width: 768px) {
        :host {
          text-align: left;
          padding: 24px;
        }

        #heading {
          --font-size: var(--text-22px);
        }

        #actions {
          justify-content: flex-start;
        }
      }
    `,
  ];

  static $html = html`
    <h2 id="heading"></h2>
    <p id="subtext"></p>
    <div id="actions"></div>
  `;
}

export namespace LandingCtaBlock {
  export type Cta = {
    label: string;
    href: string;
  };

  export type Props = {
    heading: string;
    subtext: Option<string>;
    primaryCta: Cta;
    secondaryCta: Option<Cta>;
  };
}
